import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, X, Upload, Trash2 } from "lucide-react";
import { avatarSrc } from "@/lib/avatar";

const Field = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="text-sm text-gray-400 mb-1 block">{label}</label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="bg-white/5 border-gray-700 text-white" data-testid={testId} />
  </div>
);

export const AddMemberDialog = ({ open, onOpenChange, member, setMember, photoPreview, setPhotoPreview, onPhoto, onSubmit, onCancel }) => {
  const fileInputRef = useRef(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-gray-800 text-white" data-testid="add-member-dialog">
        <DialogHeader><DialogTitle className="text-xl font-bold neon-text">Add Team Member</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="flex justify-center">
            <div className="relative w-24 h-24 rounded-full bg-gray-800 border-2 border-dashed border-gray-600 flex items-center justify-center cursor-pointer hover:border-ism-teal transition-colors"
              onClick={() => fileInputRef.current?.click()} data-testid="add-member-photo">
              {photoPreview ? (
                <>
                  <img src={avatarSrc(photoPreview)} alt="Preview" className="w-full h-full rounded-full object-cover" />
                  <button className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1" onClick={(e) => { e.stopPropagation(); setPhotoPreview(null); }} data-testid="add-member-photo-clear">
                    <X className="h-3 w-3 text-white" />
                  </button>
                </>
              ) : (
                <div className="text-center"><Upload className="h-8 w-8 text-gray-500 mx-auto mb-1" /><span className="text-xs text-gray-500">Photo</span></div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPhoto(e)} />
          </div>
          <Field label="Name *" value={member.name} onChange={(v) => setMember({ ...member, name: v })} placeholder="John Smith" testId="add-member-name" />
          <Field label="Call Sign *" value={member.callSign} onChange={(v) => setMember({ ...member, callSign: v })} placeholder="The Closer" testId="add-member-callsign" />
          <Field label="Email" value={member.email} onChange={(v) => setMember({ ...member, email: v })} placeholder="john@company.com" testId="add-member-email" />
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={onCancel} className="flex-1 border-gray-700 text-gray-400" data-testid="add-member-cancel">Cancel</Button>
            <Button onClick={onSubmit} className="flex-1 bg-ism-teal hover:bg-ism-teal/90 text-black font-semibold" data-testid="add-member-submit">Add Member</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const EditPhotoDialog = ({ memberId, onClose, onPhoto }) => (
  <Dialog open={!!memberId} onOpenChange={onClose}>
    <DialogContent className="glass-card border-gray-800 text-white" data-testid="edit-photo-dialog">
      <DialogHeader><DialogTitle className="text-xl font-bold">Update Photo</DialogTitle></DialogHeader>
      <div className="flex flex-col items-center gap-4 mt-4">
        <div className="w-32 h-32 rounded-full bg-gray-800 border-2 border-dashed border-gray-600 flex items-center justify-center cursor-pointer hover:border-ism-teal transition-colors"
          onClick={() => document.getElementById("edit-photo-input")?.click()}>
          <div className="text-center"><Camera className="h-10 w-10 text-gray-500 mx-auto mb-2" /><span className="text-sm text-gray-500">Click to upload</span></div>
        </div>
        <input id="edit-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => onPhoto(e, memberId)} />
        <p className="text-xs text-gray-500">Max 5MB • JPG, PNG, GIF</p>
      </div>
    </DialogContent>
  </Dialog>
);

export const DeleteMemberDialog = ({ member, onClose, onConfirm }) => (
  <Dialog open={!!member} onOpenChange={onClose}>
    <DialogContent className="glass-card border-gray-800 text-white" data-testid="delete-member-dialog">
      <DialogHeader><DialogTitle className="text-xl font-bold text-red-400">Delete Team Member</DialogTitle></DialogHeader>
      <div className="mt-4">
        <p className="text-gray-300">Are you sure you want to remove <strong>{member?.name}</strong> from the team?</p>
        <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
        <div className="flex gap-3 pt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 border-gray-700 text-gray-400" data-testid="delete-member-cancel">Cancel</Button>
          <Button onClick={() => onConfirm(member.memberId, member.name)} className="flex-1 bg-red-500 hover:bg-red-600 text-white" data-testid="delete-member-confirm">
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
